/**
 * Telemetry Shortcut — detects "show gibbs log" and similar phrases
 * so the chat UI can display recent LLM call telemetry inline.
 *
 * @module gibbs/knowledge/telemetryShortcut
 */

/**
 * Phrases that trigger the telemetry log display.
 */
const TELEMETRY_PHRASES = [
    'show gibbs log',
    'show llm log',
    'gibbs telemetry',
    'show telemetry',
    'llm telemetry',
];

/**
 * Check whether a user message matches a telemetry display intent.
 *
 * @param {string} msg - User message
 * @returns {boolean}
 */
export function matchTelemetryIntent(msg) {
    const lower = (msg || '').toLowerCase().trim();
    return TELEMETRY_PHRASES.some((phrase) => lower.includes(phrase));
}

/**
 * Format an array of LLM log entries as a compact text summary.
 *
 * @param {Object[]} entries - Log entries from getLLMLog()
 * @returns {string} Markdown-formatted summary
 */
export function formatTelemetryLog(entries) {
    if (!entries || entries.length === 0) {
        return 'No LLM calls recorded yet.';
    }

    const lines = entries.map((e, i) => {
        const time = new Date(e.timestamp).toLocaleTimeString();
        const dur = e.duration_ms ? `${e.duration_ms}ms` : '?';
        const errors = [];
        if (e.parseErrors && e.parseErrors.length > 0) {
            errors.push(`parse:${e.parseErrors.length}`);
        }
        if (e.validationErrors && e.validationErrors.length > 0) {
            errors.push(`val:${e.validationErrors.length}`);
        }
        const errorStr = errors.length > 0 ? ` [${errors.join(', ')}]` : '';
        const retry = e.retryAttempt > 0 ? ` retry#${e.retryAttempt}` : '';

        return `**${i + 1}.** \`${time}\` | ${e.tier}/${e.model} | ${dur}${retry}${errorStr}\n`
            + `   "${e.userMessage.slice(0, 80)}${e.userMessage.length > 80 ? '...' : ''}"\n`
            + `   -> ${e.resultType} (${e.messagesCount} msgs, sys:${e.systemPromptLength} chars)`;
    });

    return `**Gibbs LLM Log** (last ${entries.length})\n\n${lines.join('\n\n')}`;
}
